"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";

import { Button, type PillVariant } from "./ui/Button";

/**
 * The house modal surfaces — {@link Dialog}, {@link AlertDialog}, {@link Sheet}
 * — are native `<dialog>`s opened with `showModal()`: the top layer, the focus
 * trap, the inert page behind, Escape, and the focus handed back on close are
 * all the browser's, so there is no portal and no focus library. What is ours
 * is the surface (a rounded panel on the page's own tokens, a hairline ring
 * for its edge) and the way in and out (`.modal`, globals.css).
 *
 * The parent owns `open`. Escape and a click on the scrim ASK to close
 * (`onClose`); nothing here closes by itself, so a dialog with unsaved work
 * can decline.
 *
 * In use: every confirmation is an {@link AlertDialog} (ConfirmButton, a
 * Chip's confirming remove), and a reservation's cancel is a {@link Dialog}.
 * No page opens a {@link Sheet} yet.
 */
function ModalShell({
  open,
  onClose,
  role,
  focusOnOpen = "first",
  labelledBy,
  describedBy,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  role?: "alertdialog";
  /** Where opening puts focus: the first control (or the one marked
   *  `data-autofocus`), or the panel itself, with no button lit. */
  focusOnOpen?: "first" | "panel";
  labelledBy: string;
  describedBy?: string;
  className: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      // showModal focuses the first control it finds. A surface says where a
      // hand should land instead with `data-autofocus`, or asks for the panel
      // itself: focus is inside (Tab goes to its first control, Escape
      // answers), and no button is lit to take a stray Enter.
      if (focusOnOpen === "panel") el.focus();
      else el.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, focusOnOpen]);

  // A phone's keyboard shrinks the visual viewport and lets it pan inside the
  // layout one, which would carry a panel fixed to the layout viewport under
  // the keyboard, or off the glass. So the panel's frame is the visual
  // viewport: `--vv-top` / `--vv-bottom` say how far it sits in from the
  // layout one, kept current while the panel is open, and the styles place
  // the panel by them (a panel shifts to the frame's center, a sheet runs the
  // frame's height: globals.css). Not while pinch-zoomed: there the smaller
  // viewport is the reader's own doing, and shrinking the panel into it would
  // undo the zoom.
  useEffect(() => {
    const el = ref.current;
    const vv = window.visualViewport;
    if (!open || !el || !vv) return;
    const sync = () => {
      if (Math.abs(vv.scale - 1) > 0.01) {
        el.style.removeProperty("--vv-top");
        el.style.removeProperty("--vv-bottom");
        return;
      }
      const layoutHeight = document.documentElement.clientHeight;
      el.style.setProperty("--vv-top", `${Math.max(0, vv.offsetTop)}px`);
      el.style.setProperty(
        "--vv-bottom",
        `${Math.max(0, layoutHeight - vv.offsetTop - vv.height)}px`,
      );
    };
    // One write a frame, however many steps the viewport reports in it.
    let frame = 0;
    const queue = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };
    sync();
    vv.addEventListener("resize", queue);
    vv.addEventListener("scroll", queue);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener("resize", queue);
      vv.removeEventListener("scroll", queue);
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      // Focusable from script only, for `focusOnOpen="panel"`.
      tabIndex={-1}
      role={role}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      // Escape: ask, don't close — the parent's `open` is the truth.
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // Closed some other way (a second Escape the browser will not let a
      // page refuse, a form with method="dialog"): bring the parent along.
      onClose={() => {
        if (open) onClose();
      }}
      // The scrim is the dialog's own box outside the panel: the panel is a
      // child that fills it, so only a click on the scrim has the dialog
      // itself as its target.
      onClick={(e) => {
        // A modal's clicks are its own. The browser paints it in the top
        // layer, but to React it is still a child of whatever opened it (a
        // row that opens a page), and a click on the panel or the scrim must
        // not reach that, nor a page-wide click listener (the scanner's
        // focus offer would pull focus out of the dialog). `click` only: the
        // popovers close on pointerdown / mousedown, which still travel, so
        // one placed inside a dialog closes as it does anywhere.
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      className={`modal border-0 bg-bg p-0 text-sm text-fg outline-none ring-1 ring-fg/10 dark:bg-bg-muted ${className}`}
    >
      {children}
    </dialog>
  );
}

/** The quiet × in a panel's corner. Last in the markup though it sits first
 *  on screen, so opening lands on the panel's content, not on the way out. */
function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    // The pill is `relative` (its busy ring hangs off it), so the corner is a
    // wrapper's to take.
    <span className="absolute top-3 right-3 flex">
      <Button
        size="icon"
        aria-label="Close"
        title="Close this panel."
        onClick={onClose}
      >
        ×
      </Button>
    </span>
  );
}

/** A panel's foot: the actions on a faint band that closes the panel,
 *  stacked primary-first on a phone, in a row at the right from `sm` up. */
function ModalFooter({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex flex-col-reverse gap-2 border-t border-line bg-bg-muted/50 p-4 sm:flex-row sm:justify-end dark:bg-line/50 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

/** A panel taller than its frame (the visual viewport, see ModalShell) scrolls
 *  inside itself, a margin short of the frame's edges, and keeps the scroll:
 *  what runs off its end does not pass to the page behind (which an open
 *  modal holds still anyway, globals.css). */
const PANEL_SCROLL =
  "max-h-[calc(100dvh-var(--vv-top,0px)-var(--vv-bottom,0px)-2rem)] overflow-y-auto overscroll-contain";

const DIALOG_WIDTHS = new Map([
  ["sm", "sm:max-w-sm"],
  ["md", "sm:max-w-md"],
  ["lg", "sm:max-w-lg"],
] as const);

/**
 * A task that needs protected focus: a short edit made in place, a form too
 * small for a page. Escape, the ×, and a click on the scrim ask to close.
 * Give the field a hand should land on `data-autofocus`. Not for a message
 * (that is a toast or a note beside the control) and not for a yes/no — that
 * is {@link AlertDialog}.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = "sm",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One line under the title: what saving will do. */
  description?: string;
  /** The actions, primary last. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  children?: ReactNode;
}) {
  const titleId = useId();
  const descId = useId();
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={description ? descId : undefined}
      className={`modal-pop m-auto w-full max-w-[calc(100%-2rem)] ${PANEL_SCROLL} rounded-xl ${DIALOG_WIDTHS.get(size)}`}
    >
      <div className="relative">
        <div className="grid gap-4 p-4">
          <div className="flex flex-col gap-2 pr-8">
            <h2
              id={titleId}
              className="text-base leading-none font-medium text-fg"
            >
              {title}
            </h2>
            {description && (
              <p id={descId} className="text-sm text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {children}
        </div>
        {footer && <ModalFooter>{footer}</ModalFooter>}
        <CloseButton onClose={onClose} />
      </div>
    </ModalShell>
  );
}

/** The verb is the panel's primary, so it is filled. A caller names the stakes
 *  in the look its own trigger wears, and an outline look takes its filled
 *  twin here: a quiet red "delete" pill asks through a solid red "Delete". */
const FILLED = new Map<PillVariant, PillVariant>([
  ["outline", "solid"],
  ["danger", "danger-solid"],
]);

/**
 * A confirmation that wants an answer: what is about to happen, and the verb.
 * One button: the ways out are the × in the corner, Escape, and a click on
 * the scrim, and all three are `onCancel`. Opening lands on the panel itself,
 * not on a button, so Enter out of habit does nothing; Tab reaches the verb.
 * `variant` colors the verb by its stakes (`danger` for what cannot be
 * undone), and the verb is always filled ({@link FILLED}).
 */
export function AlertDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel,
  variant = "solid",
  busy = false,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  /** What happens, and whether it can be undone. */
  description: string;
  /** The verb, not "OK": "Delete", "Release". */
  confirmLabel: string;
  variant?: PillVariant;
  /** The action is in flight: the verb wears the busy ring, and neither it
   *  nor the ways out answer again. */
  busy?: boolean;
}) {
  const titleId = useId();
  const descId = useId();
  const cancel = () => {
    if (!busy) onCancel();
  };
  return (
    <ModalShell
      open={open}
      onClose={cancel}
      role="alertdialog"
      focusOnOpen="panel"
      labelledBy={titleId}
      describedBy={descId}
      className={`modal-pop m-auto w-full max-w-xs ${PANEL_SCROLL} rounded-xl sm:max-w-sm`}
    >
      <div className="relative">
        <div className="grid gap-1.5 p-4 pr-12">
          <h2 id={titleId} className="text-base font-medium text-fg">
            {title}
          </h2>
          <p id={descId} className="text-sm text-pretty text-fg-muted">
            {description}
          </p>
        </div>
        <ModalFooter>
          <Button
            variant={FILLED.get(variant) ?? variant}
            onClick={onConfirm}
            busy={busy}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
        <CloseButton onClose={cancel} />
      </div>
    </ModalShell>
  );
}

export type SheetSide = "right" | "left" | "top" | "bottom";

/** A side sheet runs the frame's full height: top to bottom of the visual
 *  viewport (see ModalShell), so its foot stays above a phone's keyboard. */
const SHEET_FULL_HEIGHT =
  "top-[var(--vv-top,0px)] bottom-[var(--vv-bottom,0px)] h-auto max-h-none";

/** Where a sheet sits, its edge rule, and the way it comes in. */
const SHEET_SIDES = new Map<SheetSide, string>([
  [
    "right",
    `modal-from-right ${SHEET_FULL_HEIGHT} right-0 left-auto w-3/4 border-l border-line sm:max-w-sm`,
  ],
  [
    "left",
    `modal-from-left ${SHEET_FULL_HEIGHT} right-auto left-0 w-3/4 border-r border-line sm:max-w-sm`,
  ],
  [
    "top",
    "modal-from-top inset-x-0 top-[var(--vv-top,0px)] bottom-auto w-full max-w-none border-b border-line",
  ],
  [
    "bottom",
    "modal-from-bottom inset-x-0 top-auto bottom-[var(--vv-bottom,0px)] w-full max-w-none border-t border-line",
  ],
]);

/**
 * A panel that slides in from an edge and keeps the page in view behind it:
 * filters on a phone, a side editor beside the list it edits. Escape, the ×,
 * and a click on the scrim ask to close. `side` picks the edge.
 */
export function Sheet({
  open,
  onClose,
  side = "right",
  title,
  description,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  side?: SheetSide;
  title: string;
  description?: string;
  /** Pinned to the sheet's foot. */
  footer?: ReactNode;
  children?: ReactNode;
}) {
  const titleId = useId();
  const descId = useId();
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={description ? descId : undefined}
      className={`fixed m-0 shadow-lg ring-0 ${SHEET_SIDES.get(side)}`}
    >
      <div className="relative flex h-full flex-col gap-4">
        <div className="flex flex-col gap-0.5 p-4 pr-12">
          <h2 id={titleId} className="text-base font-medium text-fg">
            {title}
          </h2>
          {description && (
            <p id={descId} className="text-sm text-fg-muted">
              {description}
            </p>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">
          {children}
        </div>
        {footer && (
          <div className="mt-auto flex flex-col gap-2 p-4">{footer}</div>
        )}
        <CloseButton onClose={onClose} />
      </div>
    </ModalShell>
  );
}
